import { useId, useState } from "react";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

import { auditionRatingError, useSaveAuditionRating } from "./auditionApi";
import { auditionSampleKey, type AuditionRating } from "./auditionReviews";
import type { SoundAuditionVoice } from "./contracts";
import { VoicePreviewButton } from "./VoicePreviewButton";

export function SoundAuditionReview({
  packId,
  manifestDigest,
  sample,
  voiceName,
  savedRating,
  available,
}: {
  packId: string;
  manifestDigest: string;
  sample: SoundAuditionVoice;
  voiceName: string;
  savedRating?: AuditionRating;
  available: boolean;
}) {
  const id = useId();
  const [rating, setRating] = useState("");
  const [comment, setComment] = useState("");
  const mutation = useSaveAuditionRating();
  const canReview =
    available && sample.status === "available" && !!sample.preview_url;
  const sampleName = `${voiceName} — ${sample.profile_id} (seed ${sample.seed})`;

  return (
    <article
      className="space-y-3 rounded-md border p-3"
      aria-label={sampleName}
    >
      <div className="flex items-center gap-3">
        <VoicePreviewButton
          voiceId={`${packId}:${manifestDigest}:${auditionSampleKey(sample)}`}
          voiceName={sampleName}
          previewUrl={sample.preview_url}
        />
        <div className="min-w-0">
          <p className="font-medium">{voiceName}</p>
          <p className="break-all text-xs text-muted-foreground">
            {sample.profile_id} · seed {sample.seed}
          </p>
          <p className="text-xs">
            {savedRating
              ? `Recorded review: ${savedRating.rating}/5`
              : "Not reviewed"}
          </p>
        </div>
      </div>
      {!canReview ? (
        <p className="text-sm">
          Sample unavailable. Use Refresh samples to retry.
        </p>
      ) : null}
      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={async (event) => {
          event.preventDefault();
          if (!canReview || !rating || mutation.isPending) return;
          try {
            await mutation.mutateAsync({
              pack_id: packId,
              manifest_digest: manifestDigest,
              voice_id: sample.voice_id,
              profile_id: sample.profile_id,
              seed: sample.seed,
              sample_version: sample.sample_version,
              render_profile_digest: sample.render_profile_digest,
              rating: Number(rating) as AuditionRating["rating"],
              comment: comment.trim(),
            });
            setRating("");
            setComment("");
          } catch {
            /* The mutation renders an actionable error and retains the form. */
          }
        }}
      >
        <div className="space-y-1">
          <label className="block text-sm" htmlFor={`${id}-rating`}>
            Rating
          </label>
          <select
            id={`${id}-rating`}
            className="h-9 rounded-md border bg-background px-2 text-sm"
            value={rating}
            disabled={!canReview || mutation.isPending}
            onChange={(event) => setRating(event.target.value)}
          >
            <option value="">Choose a rating</option>
            <option value="1">1 — Poor</option>
            <option value="2">2 — Weak</option>
            <option value="3">3 — Acceptable</option>
            <option value="4">4 — Good</option>
            <option value="5">5 — Excellent</option>
          </select>
        </div>
        <div className="min-w-48 flex-1 space-y-1">
          <label className="block text-sm" htmlFor={`${id}-comment`}>
            Listening notes (optional)
          </label>
          <Textarea
            id={`${id}-comment`}
            value={comment}
            maxLength={500}
            rows={1}
            disabled={!canReview || mutation.isPending}
            onChange={(event) => setComment(event.target.value)}
          />
        </div>
        <Button
          type="submit"
          disabled={!canReview || !rating || mutation.isPending}
        >
          {mutation.isPending ? "Saving…" : "Save review"}
        </Button>
      </form>
      {mutation.isError ? (
        <p role="alert" className="text-sm text-destructive">
          {auditionRatingError(mutation.error)}
        </p>
      ) : null}
      {mutation.isSuccess ? (
        <p role="status" className="text-sm">
          Review saved.
        </p>
      ) : null}
    </article>
  );
}
